import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../hooks/useAuth';
import { Loader2, Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button, Input, Label, toast } from '@repo/ui';
import { useTranslation } from 'react-i18next';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'validation.required' })
    .pipe(
      z
        .string()
        .regex(
          /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          'validation.invalidEmail'
        )
    ),
  password: z.string().min(1, { message: 'validation.required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onForgotPassword }) => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: async (values) => {
      const result = await loginSchema.safeParseAsync(values);

      if (result.success) {
        return {
          values: result.data,
          errors: {},
        };
      }

      const errors = result.error.issues.reduce((acc: any, issue) => {
        const path = issue.path[0];
        if (path) {
          acc[path] = {
            type: issue.code,
            message: issue.message,
          };
        }
        return acc;
      }, {});

      return {
        values: {},
        errors,
      };
    },
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      await login(data);
      toast.success(t('auth.loginSuccess'));
      onSuccess?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('auth.invalidCredentials'));
    }
  };

  const onError = (errors: any) => {
    // Validation errors are handled by RHF and displayed in UI
    console.debug('Validation failed', errors);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(onSubmit, onError)(e);
  };

  return (
    <div className="w-full">
      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="space-y-4">
          <Label htmlFor="email" className="mb-1.5 block">
            {t('auth.email')}
          </Label>
          <Input
            {...register('email')}
            id="email"
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            icon={<Mail className="h-4 w-4" />}
            error={!!errors.email}
            className="h-11"
          />
          {errors.email && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">{t(errors.email.message as string)}</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Label htmlFor="password" className="mb-1.5 block">
            {t('auth.password')}
          </Label>
          <Input
            {...register('password')}
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('auth.passwordPlaceholder')}
            icon={<Lock className="h-4 w-4" />}
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
            }
            error={!!errors.password}
            className="h-11"
          />
          {errors.password && (
            <div className="flex items-center gap-2 text-red-500 mt-1.5 pl-1 fade-in slide-in-from-top-1 duration-200">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm font-medium">
                {t(errors.password.message as string, { count: 6 })}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          {onForgotPassword && (
            <Button
              variant="link"
              className="px-0 cursor-pointer font-semibold h-auto"
              onClick={onForgotPassword}
              type="button"
            >
              {t('auth.forgotPassword')}
            </Button>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full p bg-[oklch(0.34_0.17_262.91)] hover:bg-[oklch(0.34_0.17_262.91)]/90 text-white shadow-lg shadow-[oklch(0.34_0.17_262.91)]/20 -mt-3 cursor-pointer"
          size="sm"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin mx-2" />
              {t('auth.signingIn')}
            </>
          ) : (
            t('auth.login')
          )}
        </Button>
      </form>
    </div>
  );
};
